import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    SourceCredibility,
    SourceCredibilityUpdated,
    SourceCredibilityOnServerCreated,
    selectSourceCredibilityActionLoading,
    selectSourceCredibilityById,
    selectLastCreatedSourceCredibilityId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-source-credibility-edit',
    templateUrl: './source-credibility-edit.component.html',
    styleUrls: ['./source-credibility-edit.component.scss'],
})


export class SourceCredibilityEditComponent implements OnInit, OnDestroy {

    model: SourceCredibility;
    oldModel: SourceCredibility;

    sourceCredibilityForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectSourceCredibilityActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectSourceCredibilityById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initSourceCredibility();
                    }
                });
            } else {
                this.model = new SourceCredibility();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initSourceCredibility();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initSourceCredibility() {
        this.createForm();
    }


    createForm() {
        this.sourceCredibilityForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Source Credibility '${
                this.model.name
                }'`;
        }
        return 'New Source Credibility';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.sourceCredibilityForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.sourceCredibilityForm.markAsPristine();
        this.sourceCredibilityForm.markAsUntouched();
        this.sourceCredibilityForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.sourceCredibilityForm.controls;
        /** check form */
        if (this.sourceCredibilityForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateSourceCredibility(this.model);
        } else {
            this.createSourceCredibility(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: SourceCredibilityModel
     */
    updateSourceCredibility(_model: SourceCredibility) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateSourceCredibility: Update<SourceCredibility> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new SourceCredibilityUpdated({
            partialSourceCredibility: updateSourceCredibility,
            sourceCredibility: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: SourceCredibilityModel
     */
    createSourceCredibility(_model: SourceCredibility) {
        this.store.dispatch(new SourceCredibilityOnServerCreated({sourceCredibility: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedSourceCredibilityId),
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
